/**
 * Renders EXACTLY what `assemble_knowledge()` would hand the AI
 * recovery assistant for one (signature, site) pair — doctrine,
 * approved lessons, and outcome statistics.
 *
 * Extracted unchanged from `KnowledgePane` so the Diagnose issue
 * detail can show the same prompt content for an issue's
 * `derived_signature` (the backend derives it with the SAME logic the
 * analysis job uses, so what this shows is what the model gets).
 * Self-contained: keyed only on its props, fetches via
 * `previewKnowledge`.
 */
import { useQuery } from '@tanstack/react-query';
import { EmptyState, SkeletonLoader } from '@shared/design-system/components';
import { previewKnowledge } from '../api/supportClient';

export function KnowledgePreview({ signature, site }: { signature: string; site: string }) {
  const previewQuery = useQuery({
    queryKey: ['support', 'knowledge-preview', signature, site],
    queryFn: () => previewKnowledge(signature, site || undefined),
  });

  if (previewQuery.isLoading) return <SkeletonLoader shape="card-block" />;
  if (previewQuery.isError) {
    return <EmptyState title="Could not load preview" description={String(previewQuery.error)} />;
  }
  const p = previewQuery.data!;

  return (
    <div>
      <p style={{ fontSize: 12, color: 'var(--nx-muted)' }}>
        This is EXACTLY what the AI assistant will be handed for this signature — not a
        summary or approximation.
      </p>
      {!p.knowledge_available && (
        <p style={{ color: 'var(--nx-warning)' }}>
          No doctrine, approved lessons, or prior outcomes exist for this signature yet. The
          assistant will triage with live tools alone and lower confidence accordingly.
        </p>
      )}

      <h4 style={{ marginBottom: 4 }}>Doctrine</h4>
      {p.doctrine ? (
        <>
          <div style={{ fontSize: 13, color: 'var(--nx-muted)', marginBottom: 4 }}>{p.doctrine_title}</div>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              background: 'var(--nx-bg3)',
              padding: 10,
              fontSize: 12,
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            {p.doctrine}
          </pre>
        </>
      ) : (
        <p style={{ color: 'var(--nx-muted)' }}>
          No doctrine file for this signature yet (git-tracked, edited via pull request in
          nexsoss-rmf).
        </p>
      )}

      <h4 style={{ marginBottom: 4, marginTop: 16 }}>Approved lessons in this prompt</h4>
      {p.lessons.length === 0 ? (
        <p style={{ color: 'var(--nx-muted)' }}>None approved yet for this signature/site.</p>
      ) : (
        <ul style={{ paddingLeft: 18 }}>
          {p.lessons.map((l) => (
            <li key={l.id} style={{ fontSize: 13, marginBottom: 4 }}>
              {l.body}
              {l.site ? ` (${l.site})` : ' (cross-site)'}
            </li>
          ))}
        </ul>
      )}

      <h4 style={{ marginBottom: 4, marginTop: 16 }}>Outcome statistics</h4>
      {p.outcome_statistics.length === 0 ? (
        <p style={{ color: 'var(--nx-muted)' }}>No recorded runbook outcomes for this signature yet.</p>
      ) : (
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--nx-muted)' }}>
              <th>Runbook</th>
              <th>Site</th>
              <th>Success rate</th>
              <th>Unassessed</th>
              <th>Last outcome</th>
            </tr>
          </thead>
          <tbody>
            {p.outcome_statistics.map((s, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--nx-border)' }}>
                <td>{s.runbook_id}</td>
                <td>{s.site}</td>
                <td>
                  {s.assessed_attempts > 0 ? `${s.successes}/${s.assessed_attempts}` : '—'}
                </td>
                <td>{s.unassessed_attempts}</td>
                <td>
                  {s.last_success_ms && (!s.last_failure_ms || s.last_success_ms > s.last_failure_ms)
                    ? `succeeded ${new Date(s.last_success_ms).toLocaleString()}`
                    : s.last_failure_ms
                      ? `failed ${new Date(s.last_failure_ms).toLocaleString()}`
                      : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
